import { AddIcon } from '@trading-ui/icons';
import { TradingButton, TradingText } from '@trading-ui/core';
import { useTranslation } from 'react-i18next';

import { CompanyCupsTable, CompanyUsersTable } from '../../tables';

import {
  CreateCompanyCupsModal,
  UpdateCompanyCupsModal,
  UpdateCompanyUsersModal,
  CreateCompanyUsersModal,
} from '../../modals';

import { getCompanyCupsColumns } from '../../tables/company-cups-table/constants/columns';
import { getCompanyUsersColumns } from '../../tables/company-users-table/constants/columns';

import useSettingsStore from '../../../store/useSettingsStore';
import useCompanyDetails from '../../../hooks/api/useCompanyDetails';

import * as styles from './CompanySelectedPanel.css';

type Props = {
  companyId: number;
};

export default function CompanySelectedPanel({ companyId }: Props) {
  const { t } = useTranslation();

  const isOpenCompanyCupsModal = useSettingsStore((state) => state.isOpenCompanyCupsModal);
  const setIsOpenCompanyCupsModal = useSettingsStore((state) => state.setIsOpenCompanyCupsModal);
  const isOpenCompanyUsersModal = useSettingsStore((state) => state.isOpenCompanyUsersModal);
  const setIsOpenCompanyUsersModal = useSettingsStore((state) => state.setIsOpenCompanyUsersModal);
  const selectedCups = useSettingsStore((state) => state.selectedCups);
  const setSelectedCups = useSettingsStore((state) => state.setSelectedCups);
  const selectedUser = useSettingsStore((state) => state.selectedCompanyUser);
  const setSelectedUser = useSettingsStore((state) => state.setSelectedCompanyUser);

  const {
    companyUsers,
    companyPlants,
    isLoading,
    isRefetching,
    deletingCupsId,
    deletingUserId,
    handleCreatePlant,
    handleUpdatePlant,
    handleDeletePlant,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
  } = useCompanyDetails(companyId);

  return (
    <>
      <div className={styles.tableContainer}>
        <div className={styles.tableTitle}>
          <TradingText text={t('settings.companyDetailsTab.companyCups.title')} variant="h6" />
          <TradingButton
            label={t('settings.companyDetailsTab.companyCups.addCupsButton')}
            handleClick={() => setIsOpenCompanyCupsModal(true)}
            startIcon={<AddIcon size={16} />}
            disabled={isLoading}
          />
        </div>
        <CompanyCupsTable
          columns={getCompanyCupsColumns({
            setSelectedCups,
            handleDeletePlant,
            deletingId: deletingCupsId,
          })}
          data={companyPlants}
          isLoading={isLoading}
          isRefetching={isRefetching}
        />
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.tableTitle}>
          <TradingText text={t('settings.companyDetailsTab.companyUsers.title')} variant="h6" />
          <TradingButton
            label={t('settings.companyDetailsTab.companyUsers.addUsersButton')}
            handleClick={() => setIsOpenCompanyUsersModal(true)}
            startIcon={<AddIcon size={16} />}
            disabled={isLoading}
          />
        </div>
        <CompanyUsersTable
          columns={getCompanyUsersColumns({
            setSelectedUser,
            handleDeleteUser,
            deletingId: deletingUserId,
          })}
          data={companyUsers}
          isLoading={isLoading}
          isRefetching={isRefetching}
        />
      </div>
      {isOpenCompanyCupsModal && (
        <CreateCompanyCupsModal
          setIsOpen={setIsOpenCompanyCupsModal}
          companyUsers={companyUsers}
          handleCreatePlant={handleCreatePlant}
          companyId={companyId}
        />
      )}
      {isOpenCompanyUsersModal && (
        <CreateCompanyUsersModal
          setIsOpen={setIsOpenCompanyUsersModal}
          companyPlants={companyPlants}
          handleCreateUser={handleCreateUser}
          companyId={companyId}
        />
      )}
      {selectedCups && (
        <UpdateCompanyCupsModal
          selectedCups={selectedCups}
          setSelectedCups={setSelectedCups}
          companyUsers={companyUsers}
          handleUpdatePlant={handleUpdatePlant}
          companyId={companyId}
        />
      )}
      {selectedUser && (
        <UpdateCompanyUsersModal
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          companyPlants={companyPlants}
          handleUpdateUser={handleUpdateUser}
          companyId={companyId}
        />
      )}
    </>
  );
}
